/**
 * PDP insurance toggle controller
 * Business rules:
 * - AC only, or SW (where SW pricing includes AC).
 * - AC and SW cannot be selected at the same time.
 * - Keep top/bottom displayed totals in sync for standard PDP and CTO PDP.
 */
(function () {
  const MODE_NONE = 'none';
  const MODE_APPLECARE = 'applecare';
  const MODE_SECONDARY_WARRANTY = 'secondary-warranty';

  /* COSMONET Custom Implementation: accept only known insurance modes and fall back to live checkbox state after async markup refreshes. */
  function normalizeMode(mode) {
    if ([MODE_NONE, MODE_APPLECARE, MODE_SECONDARY_WARRANTY].includes(mode)) {
      return mode;
    }

    return getCurrentMode();
  }
  /* end COSMONET Custom Implementation */

  function getAppleCareInput() {
    return document.getElementById('add-apple-care');
  }

  function getSecondaryWarrantyInput() {
    return document.getElementById('add-secWarranty');
  }

  function getCurrentMode() {
    const appleCareInput = getAppleCareInput();
    const secondaryWarrantyInput = getSecondaryWarrantyInput();
    const hasAppleCare = Boolean(appleCareInput?.checked);
    const hasSecondaryWarranty = Boolean(secondaryWarrantyInput?.checked);

    if (hasSecondaryWarranty) {
      return MODE_SECONDARY_WARRANTY;
    }

    if (hasAppleCare) {
      return MODE_APPLECARE;
    }

    return MODE_NONE;
  }

  function setMode(mode) {
    const appleCareInput = getAppleCareInput();
    const secondaryWarrantyInput = getSecondaryWarrantyInput();

    if (appleCareInput) {
      appleCareInput.checked = mode === MODE_APPLECARE;
    }

    if (secondaryWarrantyInput) {
      secondaryWarrantyInput.checked = mode === MODE_SECONDARY_WARRANTY;
    }
  }

  function setHiddenVariantIds(mode, sourceElement) {
    const appleCareVariantInput = document.getElementById('apple-care-varid');
    const secondaryWarrantyVariantInput = document.getElementById('secWarranty-varid');
    const appleCareContainer = sourceElement?.closest?.('#apple-care-add') || document.getElementById('apple-care-add');
    const secondaryWarrantyContainer =
      sourceElement?.closest?.('#secWarranty-add') || document.getElementById('secWarranty-add');

    if (appleCareVariantInput) {
      appleCareVariantInput.value =
        mode === MODE_APPLECARE ? String(appleCareContainer?.getAttribute('data-index') || '') : '';
    }

    if (secondaryWarrantyVariantInput) {
      secondaryWarrantyVariantInput.value =
        mode === MODE_SECONDARY_WARRANTY
          ? String(secondaryWarrantyContainer?.getAttribute('data-index') || '')
          : '';
    }
  }

  function syncStandardButtons(mode) {
    const appleCareContainer = document.getElementById('apple-care-add');
    const secondaryWarrantyContainer = document.getElementById('secWarranty-add');

    const appleCareLabelAdd =
      appleCareContainer?.getAttribute('data-label-add') ||
      document.querySelector('#apple-care-add .pdp-care-btn')?.textContent ||
      'Add';
    const appleCareLabelAdded =
      appleCareContainer?.getAttribute('data-label-added') ||
      document.querySelector('#apple-care-add .pdp-care-btn')?.textContent ||
      'Added';
    const secondaryWarrantyLabelAdd =
      secondaryWarrantyContainer?.getAttribute('data-label-add') ||
      document.querySelector('#secWarranty-add .pdp-secWarranty-btn')?.textContent ||
      'Add';
    const secondaryWarrantyLabelAdded =
      secondaryWarrantyContainer?.getAttribute('data-label-added') ||
      document.querySelector('#secWarranty-add .pdp-secWarranty-btn')?.textContent ||
      'Added';

    document.querySelectorAll('#apple-care-add .pdp-care-btn').forEach((button) => {
      button.textContent = mode === MODE_APPLECARE ? appleCareLabelAdded : appleCareLabelAdd;
    });

    document.querySelectorAll('#secWarranty-add .pdp-secWarranty-btn').forEach((button) => {
      button.textContent = mode === MODE_SECONDARY_WARRANTY ? secondaryWarrantyLabelAdded : secondaryWarrantyLabelAdd;
    });
  }

  function syncCtoButtonClasses(mode) {
    const appleCareLabel = document.querySelector('#apple-care-add > label');
    const secondaryWarrantyLabel = document.querySelector('#secWarranty-add > label');
    const removeInsuranceCheckbox = document.getElementById('remove-insurance');
    const removeInsuranceLabel = document.querySelector('#insurance-remove > label');

    if (appleCareLabel) {
      appleCareLabel.classList.toggle('checked', mode === MODE_APPLECARE);
    }

    if (secondaryWarrantyLabel) {
      secondaryWarrantyLabel.classList.toggle('checked', mode === MODE_SECONDARY_WARRANTY);
    }

    if (removeInsuranceCheckbox) {
      removeInsuranceCheckbox.checked = mode === MODE_NONE;
    }

    if (removeInsuranceLabel) {
      removeInsuranceLabel.classList.toggle('checked', mode === MODE_NONE);
    }
  }

  function syncCtoSummary(mode) {
    const selectedOption = document.querySelector('.js-cto-insurance-selected-option');
    const summaryProtectionValue = document.querySelector('.js-cto-summary-protection-value');
    const summaryProtection = document.querySelector('.js-cto-summary-protection');
    if (!selectedOption || !summaryProtectionValue || !summaryProtection) {
      return;
    }

    if (mode === MODE_SECONDARY_WARRANTY) {
      selectedOption.innerHTML = 'AppleCare+</br>Warranty';
      summaryProtectionValue.textContent = 'AppleCare+ & Warranty';
      summaryProtection.classList.remove('hidden');
      return;
    }

    if (mode === MODE_APPLECARE) {
      selectedOption.textContent = 'AppleCare+';
      summaryProtectionValue.textContent = 'AppleCare+';
      summaryProtection.classList.remove('hidden');
      return;
    }

    selectedOption.textContent = SDG?.Data?.ctoNoCoverage || 'No coverage';
    summaryProtectionValue.textContent = SDG?.Data?.ctoNoCoverage || 'No coverage';
    summaryProtection.classList.remove('hidden');
  }

  function updateStandardPdpPrices(mode) {
    // CTO pricing has its own calculator and formatting pipeline.
    if (document.querySelector('.apl-section-product-cto') && typeof window.updateAllCTOPriceDisplays === 'function') {
      return;
    }

    const hasTradeIn =
      $('#selected-trade-in-value').val() &&
      $('#selected-trade-in-value').val() !== '0' &&
      $('#selected-trade-in-device').val() !== '';

    let attributeKey = 'data-price';
    if (mode === MODE_APPLECARE) {
      attributeKey = 'data-price-with-care';
    } else if (mode === MODE_SECONDARY_WARRANTY) {
      // SW mode includes AC pricing by contract.
      attributeKey = 'data-price-with-secWarranty';
    }

    document.querySelectorAll('.js-product-price-with-care-warranty').forEach((priceElement) => {
      let nextPrice = null;

      if (hasTradeIn && typeof window.setPriceWithTradeIn === 'function') {
        nextPrice = window.setPriceWithTradeIn(attributeKey, priceElement);
      }

      if (!nextPrice) {
        nextPrice = priceElement.getAttribute(attributeKey);
      }

      if (!nextPrice && mode === MODE_SECONDARY_WARRANTY) {
        nextPrice = priceElement.getAttribute('data-price-with-care-and-secWarranty');
      }

      if (!nextPrice) {
        nextPrice = priceElement.getAttribute('data-price');
      }

      if (nextPrice) {
        priceElement.innerHTML = nextPrice;
      }
    });
  }

  function updateCtoPrices() {
    if (typeof window.updateAllCTOPriceDisplays === 'function') {
      window.updateAllCTOPriceDisplays();
    }
  }

  function applyMode(mode, sourceElement) {
    setMode(mode);
    setHiddenVariantIds(mode, sourceElement);
    syncStandardButtons(mode);
    syncCtoButtonClasses(mode);
    syncCtoSummary(mode);
    updateCtoPrices();
    updateStandardPdpPrices(mode);
  }

  function toggleMode(nextMode, sourceElement) {
    const currentMode = getCurrentMode();
    const mode = currentMode === nextMode ? MODE_NONE : nextMode;
    applyMode(mode, sourceElement);
  }

  function bindEvents() {
    $(document).on('click', '#apple-care-add', function (event) {
      event.preventDefault();
      toggleMode(MODE_APPLECARE, event.currentTarget);
    });

    $(document).on('click', '#secWarranty-add', function (event) {
      event.preventDefault();
      toggleMode(MODE_SECONDARY_WARRANTY, event.currentTarget);
    });

    $(document).on('click', '#insurance-remove', function (event) {
      event.preventDefault();
      applyMode(MODE_NONE, event.currentTarget);
    });

    $(document).on('change', '#add-apple-care, #add-secWarranty', function () {
      applyMode(getCurrentMode(), this);
    });

    // Trade-in scripts update hidden fields; refresh displayed price after those updates.
    $(document).on('click', '#no-trade-in', function () {
      window.setTimeout(() => {
        applyMode(getCurrentMode(), null);
      }, 0);
    });

    /* COSMONET Custom Implementation: re-apply the selected insurance mode after variant AJAX updates replace PDP content. */
    document.addEventListener('pdp:insurance-content-updated', (event) => {
      applyMode(normalizeMode(event.detail?.mode), null);
    });
    /* end COSMONET Custom Implementation */
  }

  function init() {
    bindEvents();
    applyMode(getCurrentMode(), null);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* COSMONET Custom Implementation: expose an imperative refresh hook for future async PDP integrations. */
  window.COSMONET_PDP_INSURANCE = {
    refresh(mode) {
      applyMode(normalizeMode(mode), null);
    },
  };
  /* end COSMONET Custom Implementation */
})();
